import dayjs from 'dayjs';

import { CartType, SimpleCartStatus } from '@/constants/cart';
import Constants from '@/constants/const';
import { AddToCartReferType } from '@/constants/track';
import { CategoryType } from '@/types/category';
import { PaymentMethod } from '@/types/payment';
import callAppFunction from '@/utils/bridge';
import EventEmitter, { actions } from '@/utils/eventemitter';
import {
  isAppSupportPurchaseStatusNotify,
  isAppSupportCollectStatusNotify,
  isAppSupportOnPopupWindowDidFinish
} from '@/utils/weeeapp-notify';

import { isSayWeeeApp, isServer } from '@/utils/common/device';

import { getCookie, getValueFromCookie, setCookie } from '@/utils/cookie';

function isRealArray(arr) {
  return !!arr && Array.isArray(arr) && arr.length > 0;
}

function createActivityMap(activities) {
  const id2qty = {};
  if (isRealArray(activities)) {
    for (const activity of activities) {
      const items = activity?.items;
      if (isRealArray(items)) {
        for (const item of items) {
          if (item.status === SimpleCartStatus.canceled || item.vendor_is_opened === false) {
            continue;
          }
          //不是赠品才计算数量
          if (!item?.is_gift) {
            id2qty[getUniqueProductId(item?.product_id, item?.product_key)] = item?.quantity;
          }
        }
      }
    }
  }
  return id2qty;
}

function createNormalItemsMap(items, type = null) {
  const id2qty = {};
  if (isRealArray(items)) {
    for (const item of items) {
      if (item.status === SimpleCartStatus.canceled || item.vendor_is_opened === false) {
        continue;
      }
      if (type === 'addon') {
        id2qty[item?.product_id] = item?.quantity;
      } else id2qty[getUniqueProductId(item?.product_id, item?.product_key)] = item?.quantity;
    }
  }
  return id2qty;
}

export function createProduct2Qty(cartInfo: any): ObjectType {
  if (!cartInfo) return null;
  const sections = cartInfo?.sections;
  if (!sections) return null;
  // let normalSection = sections.find(section => section.type === 'normal');
  if (!Array.isArray(sections) && sections.length === 0) return null;

  return sections.reduce((pre, cur) => {
    const activities = cur?.activity_info;
    const activityMap = createActivityMap(activities);
    const items = cur?.items.map(item => {
      return {
        ...item,
        vendor_is_opened:
          cur.type === CartType.hotdish ? cur.vendor_is_opened || cur?.vendor_info?.vendor_is_opened : true
      };
    });
    let normalItemsMap;
    if (cur.type === CartType.hotdish && !cur?.vendor_info?.vendor_is_opened) {
      normalItemsMap = {};
    } else {
      normalItemsMap = createNormalItemsMap(items, cur.type);
    }
    return { ...pre, ...activityMap, ...normalItemsMap };
  }, {});
}

function createCartActivityItemsMap(activities) {
  const newItems = [];
  if (isRealArray(activities)) {
    for (const activity of activities) {
      const items = activity?.items;
      if (isRealArray(items)) {
        for (const item of items) {
          if (item.status === SimpleCartStatus.canceled || item.vendor_is_opened === false) {
            continue;
          }
          //不是赠品才计算数量
          if (!item?.is_gift) {
            newItems?.push(item);
          }
        }
      }
    }
  }
  return newItems;
}

function createCartNormalItemsMap(items, type = null) {
  const newItems = [];
  if (isRealArray(items)) {
    for (const item of items) {
      if (item.status === SimpleCartStatus.canceled || item.vendor_is_opened === false) {
        continue;
      }
      newItems?.push(item);
    }
  }
  return newItems;
}

export function createAllCartInfoItems(cartInfo: any): ObjectType {
  if (!cartInfo) return null;
  const sections = cartInfo?.sections || [];
  if (!sections?.length) return null;

  return sections.reduce((pre, cur) => {
    const activities = cur?.activity_info;
    const activityMap = createCartActivityItemsMap(activities);
    const items = cur?.items.map(item => {
      return {
        ...item,
        vendor_is_opened:
          cur.type === CartType.hotdish ? cur.vendor_is_opened || cur?.vendor_info?.vendor_is_opened : true
      };
    });
    let normalItemsMap = [];
    if (cur.type === CartType.hotdish && !cur?.vendor_info?.vendor_is_opened) {
      normalItemsMap = [];
    } else {
      normalItemsMap = createCartNormalItemsMap(items, cur.type);
    }

    return [...pre, ...activityMap, ...normalItemsMap];
  }, []);
}

export function simpleInfoCreateProduct2Qty(simpleInfo: any): ObjectType {
  if (!simpleInfo) return null;
  const items = simpleInfo?.items;
  if (!items) return null;
  // let normalSection = sections.find(section => section.type === 'normal');
  if (!Array.isArray(items) && items.length === 0) return null;

  const id2qty = {};

  for (const element of items) {
    if (element.status === SimpleCartStatus.canceled || !element.vendor_is_opened) {
      continue;
    }
    id2qty[element?.product_id] = element?.quantity;
  }

  return id2qty;
}

export const getSalesOrgIdFromCookie = (ctx?) => {
  return getValueFromCookie(Constants.SALES_ORG_ID, ctx);
};

export const getDeliveryDateFromCookie = (ctx?) => {
  return getValueFromCookie(Constants.DELIVERY_DATE, ctx);
};

export const getZipCodeFromCookie = (ctx?) => {
  return getValueFromCookie(Constants.ZIP_CODE, ctx);
};

export const getLangFromCookie = (ctx?) => {
  return getValueFromCookie(Constants.LANGUAGE, ctx);
};

export const getFirstDeliveryDate = () => {
  const tomorrow = dayjs().add(1, 'day').format('YYYY-MM-DD');
  return tomorrow;
};

/**
 * 过滤banner中用户特性相关逻辑 与 app 展示逻辑保持一致
 * userInfo中 user_feature 和 carousel 中 feature 要一致, 否则不展示
 * 如果上面成立并且
 * 1.filter?.ethnic_group 不存在, 不限制
 * 2.filter?.ethnic_group 数组中都是空的字符串 不限制
 * 3.filter?.ethnic_group 数组中有数据, 并且userInfo中的ethnic_group在数组中 不限制
 */
export const filterDataByUserInfo = (filterData, userInfo) => {
  const { filter, user_feature } = userInfo;
  return filterData?.filter(item => {
    const carouselItemFilter = item || {};
    let isEthnicGroup = false;
    const ethnicGroup = carouselItemFilter?.filter?.ethnic_group;
    if (!ethnicGroup) {
      isEthnicGroup = true;
    } else if (ethnicGroup?.length) {
      if (!ethnicGroup.filter(Boolean).length) {
        isEthnicGroup = true;
      } else {
        if (ethnicGroup.includes(filter?.ethnic_group)) {
          isEthnicGroup = true;
        }
      }
    }

    if ((carouselItemFilter.feature & user_feature) > 0 && isEthnicGroup) {
      return true;
    }
    return false;
  });
};

export const removeTargetCategory = (target: string, originArr: CategoryType[]) => {
  const res = [];
  originArr.forEach(item => {
    if (item.key !== target) {
      res.push(item);
    }
  });
  return res;
};

/**
 * 把zh-Hant 转化为zht
 * 把zh-Hans 转化为zh
 * @param lan
 * @returns
 */
export const setLanguageToFrontend = (lan = '') => {
  if (lan === Constants.LANGUAGE_ZHT_ALIAS) {
    return Constants.LANGUAGE_CHINESE_T;
  }
  if (lan === Constants.LANGUAGE_ZH_ALIAS) {
    return Constants.LANGUAGE_CHINESE;
  }
  return lan;
};

//判断是否返利联盟用户
export const checkReferral = () => {
  return !!(getCookie(Constants.AFFILIATE_REFERRAL_ID) || '');
};

export const getDealId = (ctx?) => {
  return ctx?.req?.[Constants.DEAL_ID] || getCookie(Constants.DEAL_ID, ctx);
};

export const getZipcode = (ctx?) => {
  return ctx?.req?.[Constants.ZIP_CODE] || getCookie(Constants.ZIP_CODE, ctx);
};

export const getOrderToken = () => {
  return getCookie(Constants.ORDER_TOKEN);
};

export const getPreOrderId = () => {
  return getCookie(Constants.PRE_ORDER_ID);
};

export const getOrgId = (ctx?) => {
  return ctx?.req?.[Constants.SALES_ORG_ID] || getCookie(Constants?.SALES_ORG_ID, ctx);
};

export const isNoBundleOrg = () => {
  return ['4', '2', '7'].includes(getOrgId());
};

/**
 * 将12小时倒计时的时间戳存入cookie
 * @param key
 */
export const set12hCookie = key => {
  setCookie(key, dayjs().add(12, 'hour').unix().valueOf().toString() + ':' + getCookie(Constants.WEEE_SESSION_TOKEN));
};

/**
 * 获取cookie中的倒计时的时间戳
 * @param key
 */
export const get12Cookie = key => {
  let currentSessionToken = getCookie(Constants.WEEE_SESSION_TOKEN);
  let [value, preSessionToken] = (getCookie(key) || '').split(':');
  if (currentSessionToken === preSessionToken) {
    return value;
  }
  return '';
};

/**
 * 将12小时倒计时的时间戳存入cookie
 * @param key
 */
export const setCountDownTimeCookie = (key, hour?) => {
  let string =
    dayjs()
      .add(hour || 12, 'hour')
      .unix()
      .valueOf()
      .toString() +
    ':' +
    getCookie(Constants.WEEE_SESSION_TOKEN);
  setCookie(key, string);
  return string;
};

/**
 * 获取cookie中的倒计时的时间戳
 * @param key
 */
export const getCountDownTimeCookie = key => {
  let currentSessionToken = getCookie(Constants.WEEE_SESSION_TOKEN);
  let [value, preSessionToken] = (getCookie(key) || '').split(':');
  if (currentSessionToken === preSessionToken) {
    return value;
  }
  return '';
};

/**
 *
 * @returns 获取付费渠道数据
 */
export const getPaidWSources = () => {
  return Constants.PAID_W_SOURCES.map(item => item.value);
};

/**
 * 根据用户的 user_type 判断是否是新用户
 */
export const isNewUserCheck = (userType: string) => {
  return userType === 'new';
};

/**
 * 根据用户的 user_feature 判断是否是匿名用户
 * @param userFeature user_feature
 * @returns
 */
export const isAnonymousUserCheck = (userFeature: number) => {
  return ((userFeature || 0) & 8) > 0;
};

/**
 * 传入销售数量soldCount
 * @param soldCount
 * @returns
 */
export const getSoldCountUi = (soldCount: number) => {
  switch (true) {
    case soldCount < 50:
      return '';
    case soldCount >= 50 && soldCount <= 99:
      return '50+';
    case soldCount >= 100 && soldCount <= 999:
      return Math.round(soldCount / 100) * 100 + '+';
    case soldCount >= 1000 && soldCount <= 9999:
      return Math.round(soldCount / 1000) + 'K+';
    case soldCount >= 10000:
      return Math.round(soldCount / 10000) + 'W+';
    default:
      return;
  }
};

/*
 * 当前w_source参数是否是付费用户, 当前函数只能用在client端
 * @param source w_source
 * @returns boolean
 */
export const checkIsPaidSource = (source): boolean => {
  if (sessionStorage.getItem('isPaidSource')) {
    return true;
  }
  const isPaidSource = getPaidWSources().includes(+source || 0);
  if (isPaidSource) {
    sessionStorage.setItem('isPaidSource', '1');
    return true;
  }
  return false;
};

export const getLoginState = (ctx?) => {
  let login = false;
  try {
    login = getCookie(Constants.IS_LOGIN, ctx) === '1';
  } catch (e) {
    console.log(e?.message);
  }
  return login;
};

/**
 *
 * @returns 是否是直邮区域
 */
export const isShippingOrder = () => {
  const isShippingOrder = getCookie(Constants.IS_SHIPPING_ORDER);
  return isShippingOrder === Constants.IS_SHIPPING_ORDER_VAL.IS;
};

export const isMailOrigin = () => {
  const isShippingOrder = getCookie(Constants.IS_SHIPPING_ORDER);
  return isShippingOrder === Constants.IS_SHIPPING_ORDER_VAL.IS;
};

/**
 * 判断是否是中文、中文繁体
 * @param lang 现在的语言
 */
export const isLikeChinese = lang => {
  let likeChinese = [Constants.LANGUAGE_ZH, Constants.LANGUAGE_ZHT, Constants.LANGUAGE_ZHT_ALIAS];
  if (likeChinese.includes(lang)) {
    return true;
  }
  return false;
};

/**
 * 中文 、中文繁体显示中文，其他语言显示英文
 * @param lang 当前语言
 */
export const handleLanguage = lang => {
  if (isLikeChinese(lang)) {
    return Constants.LANGUAGE_ZH;
  }
  return Constants.LANGUAGE_EN;
};

export const AddToCartParamReferType = data => {
  return [
    {
      matched: !!data?.refer_type,
      value: data?.refer_type
    },
    {
      matched: !!data?.is_pantry,
      value: AddToCartReferType.pantry
    },
    {
      matched: data?.biz_type === 'seller',
      value: AddToCartReferType.seller
    },
    {
      matched: true,
      value: AddToCartReferType.normal
    }
  ].find(item => {
    return item.matched;
  })?.value;
};

/*  php 的pre接口default_payment_info 不包含is_check_cvv，所以要用v2接口的卡列表检查当前使用的信用卡需要校验cvv
 */
export const cardListNeedsCheckCvv = (cardList, default_payment_info, data?) => {
  if (
    cardList?.find(item => item?.profile_id === default_payment_info?.profile_id)?.is_check_cvv ||
    default_payment_info?.is_check_cvv
  )
    return true;
  return false;
};
/**
 * 支付组件 是否需要展示cvv输入框
 *
 * 展示必要条件：
 * 1支付方式必须是信用卡支付
 * 2需要支付的金额大于零，//因为支付的金额为零是用points全款支付的，不需要展示信用卡和cvv
 * 3没有产生cvvToken 或者产生的cvvToken 不是当前信用卡的，仍需要获取cvvToken，展示cvv
 */
export const showCvv = (
  payment_category,
  final_amount,
  default_payment_info,
  cvvToken,
  cvvTokenCardId,
  cardList,
  data?
) => {
  const cardNeedsCheckCvv = cardListNeedsCheckCvv(cardList, default_payment_info);
  if ([PaymentMethod.Card as string, PaymentMethod.BrainTreeCard as string].includes(payment_category)) {
    if (+final_amount > 0) {
      if (!cvvToken || (cvvToken && default_payment_info?.profile_id !== cvvTokenCardId)) {
        if (cardNeedsCheckCvv) {
          return true;
        }
      }
    }
  }
  return false;
};

/**
 * 判断是否是自己网站内的跳转
 * @param url
 */
export const isFromWeeeWebite = url => {
  if (isServer) return false;
  return (url || '').includes('sayweee.') || (url || '').includes('masgusto.');
};

/**
 *  获取store的key
 */
export const getStoreKey = (ctx?) => {
  return getCookie(Constants.WEEE_STORE, ctx) || '';
};

/**
 *
 * @param link 获取url的path部分，去掉域名和语言位
 */
export const getURLPath = link => {
  return (link || '').replace(/(http(?:s)?:\/\/)?(?:www\.)?(.*?)\//, '').replace(Constants.LANGUAGE_REGEXP_TWO, '');
};

/**
 * 弹出社区登录注册弹框
 *  @param destUrl 登录成功后跳转的url
 *  @param setPopUpType 设置登录注册tab类型
 */
export const emitLoginSignuPopup = ({ destUrl, setPopUpType }: { destUrl?: string; setPopUpType?: number }) => {
  EventEmitter.emit(actions.showLoginSignuPopup, { destUrl, setPopUpType });
};

/**
 *
 * @param productId string
 * @param productKey string
 */
export const getUniqueProductId = (productId: number | string, productKey: string) => {
  if (productKey) {
    return productKey;
  }
  if (productId) {
    productKey = `${productId}`;
    return productKey;
  }
  console.log('getUniqueProductId error');
};

/**
 *通知app手机号绑定成功
 */
export const noticeAppAccountStatus = () => {
  try {
    if (isSayWeeeApp()) {
      if (isAppSupportPurchaseStatusNotify()) {
        const args = {
          type: 'bindSuccess'
        };
        callAppFunction('accountStatusNotify', args);
      }
    }
  } catch (error) {
    console.log('notifyAppToUpdate error', error);
  }
};
/**
 * 同步app收藏列表
 */
export const appCollectStatusNotify = (ids: any[] = [], status: 'add' | 'remove' | 'reset', type = 'product') => {
  try {
    if (isSayWeeeApp()) {
      const isAppSupportCollectStatusNotifyVersion = isAppSupportCollectStatusNotify();
      if (isAppSupportCollectStatusNotifyVersion) {
        const args = {
          type: type,
          status: status,
          ids: ids.map(i => Number(i))
        };
        callAppFunction('collectStatusNotify', args);
      }
    }
  } catch (error) {
    console.log('collectStatusNotify error', error);
  }
};
/**
 * 在popup内容加载完毕后触发，APP监听js，然后显示popup
 */
export const appOnPopupWindowDidFinish = () => {
  try {
    if (isSayWeeeApp()) {
      const isAppSupportOnPopupWindowDidFinishVersion = isAppSupportOnPopupWindowDidFinish();
      if (isAppSupportOnPopupWindowDidFinishVersion) {
        const args = {};
        callAppFunction('onPopupWindowDidFinish', args);
      }
    }
  } catch (error) {
    console.log('onPopupWindowDidFinish error', error);
  }
};

/**
 * 判断已登录用户store是否是默认的
 * @param manual 记录切换store的方式
 * @param is_login ftu流程切换store，登录状态从外部传入
 * @returns
 */
export const getStoreSelectMethod = (manual, is_login?: boolean) => {
  const isLogin = !!is_login || getLoginState();
  return isLogin ? manual : void 0;
};
